import { useEffect, useRef, useState } from "react";
import { API_V1 } from "../lib/api";
import { ArrowsClockwise, Pause, Play, Funnel } from "@phosphor-icons/react";

const EVENT_TYPES = [
  "ALL",
  "photo.uploaded",
  "photo.completed",
  "photo.failed",
  "photo.deleted",
  "session.ready",
  "session.deleted",
  "sync.forwarded",
  "sync.failed",
];

const eventColor = (e) => {
  if (e.endsWith(".failed")) return "chip-live";
  if (e.startsWith("sync.")) return "chip-blue";
  if (e === "session.ready") return "chip-blue";
  return "";
};

const fmtTs = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts * 1000);
  return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const load = async (selected = filter) => {
    try {
      const url = selected === "ALL"
        ? `${API_V1}/events?limit=80`
        : `${API_V1}/events?event=${encodeURIComponent(selected)}&limit=80`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(r.status);
      const data = await r.json();
      setEvents(data.items || []);
    } catch {
      // network blip — leave existing
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter);
    if (paused) return;
    timerRef.current = setInterval(() => load(filter), 2000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, filter]);

  return (
    <div className="px-4 py-6 max-w-5xl mx-auto page-fade" data-testid="events-page">
      <div className="flex items-end justify-between mb-6 gap-3">
        <div>
          <p className="label-mono text-neutral-500">99 / EVENT STREAM</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            EVENT BUS
          </h1>
          <p className="text-xs font-mono text-neutral-500 mt-1">
            INTERNAL AUDIT LOG · POLL 2s · TTL 30d
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => setPaused((p) => !p)}
            className={paused ? "btn-flat" : "btn-flat-outline"}
            data-testid="events-pause-btn"
          >
            {paused ? <Play weight="fill" size={16} /> : <Pause weight="fill" size={16} />}
            {paused ? "Play" : "Pause"}
          </button>
          <button
            onClick={() => load(filter)}
            className="btn-flat-outline"
            data-testid="events-refresh-btn"
          >
            <ArrowsClockwise weight="bold" size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-6 pb-2 border-b-2 border-black" data-testid="events-filters">
        <Funnel weight="bold" size={16} className="mt-1" />
        {EVENT_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            className={`chip transition-colors ${filter === t ? "chip-live" : ""}`}
            data-testid={`events-filter-${t.replace(/\./g, "-")}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Terminal-style stream */}
      <div
        className="hairline-2 bg-black text-white font-mono text-[12px] leading-relaxed overflow-x-auto"
        style={{ boxShadow: "4px 4px 0 0 #FF2A2A" }}
        data-testid="events-stream"
      >
        <div className="px-3 py-2 border-b border-white/30 flex items-center justify-between bg-black">
          <span className="label-mono opacity-70">/api/v1/events</span>
          <span className="label-mono opacity-70">
            {loading ? "BOOTING…" : paused ? "PAUSED" : "LIVE"}
            {!paused && !loading && <span className="ml-1.5 inline-block w-1.5 h-1.5 bg-[#FF2A2A] live-dot" />}
          </span>
        </div>
        {events.length === 0 ? (
          <p className="px-4 py-6 text-center opacity-60">no events yet</p>
        ) : (
          <ul className="divide-y divide-white/10">
            {events.map((e) => (
              <li
                key={e.event_id}
                className="px-3 py-2 hover:bg-white/5 slide-in"
                data-testid={`event-row-${e.event_id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="opacity-60">{fmtTs(e.ts)}</span>
                  <span className={`chip ${eventColor(e.event) || "chip"}`}>{e.event}</span>
                  <span className="opacity-60">id</span>
                  <span className="text-[#FF2A2A]">{e.event_id.slice(0, 8)}</span>
                  {e.resource?.type && (
                    <>
                      <span className="opacity-60">{e.resource.type}</span>
                      <span>{(e.resource.id || "").slice(0, 8)}</span>
                    </>
                  )}
                </div>
                {e.resource?.filename && (
                  <p className="opacity-70 truncate pl-1">↳ {e.resource.filename}</p>
                )}
                {e.resource?.url && (
                  <p className="opacity-50 truncate pl-1">↳ {e.resource.url}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-neutral-500 mt-4 font-mono">
        every internal state transition is logged here even if no webhook is subscribed —
        see <code>contracts/WEBHOOKS.md §6</code>.
      </p>
    </div>
  );
}
